import { menuItem, MenuItem } from './menu-item';
import { MenuContext } from './menu-context';
import { Permission, hasPerm } from '../../session/permissions';
import { TenantStorage } from '../../session/session-decode';
import { isContaPacAutonoma, isLicencaAccessOnPro } from './licenca';

// Predicados de visibilidade extraídos de `buildCadastrosHeadSection`: os itens
// dessa seção compartilham um pequeno conjunto de combinações de guardas
// (integrador sem tenant, sem tenant, superusuário sem tenant), então nomear
// cada combinação reduz a complexidade da função que monta os itens sem
// duplicar lógica.

/** Item visível apenas para integrador sem tenant ativo, sujeito à permissão informada. */
function isVisivelIntegradorSemTenant(context: MenuContext, permission: Permission): boolean {
  return !context.possuiTenant && context.isIntegrador && hasPerm(permission, context.authorities);
}

/** Item visível sem tenant ativo (qualquer tipo de usuário), sujeito à permissão informada. */
function isVisivelSemTenant(context: MenuContext, permission: Permission): boolean {
  return !context.possuiTenant && hasPerm(permission, context.authorities);
}

/** Item de superusuário, visível apenas sem tenant ativo. */
function isVisivelSuperUsuarioSemTenant(context: MenuContext): boolean {
  return !context.possuiTenant && context.isSuperUsuario;
}

/** "Início" do integrador: sem tenant, com permissão de criar conta ou de ler recados de integrador. */
function isVisivelInicioIntegrador(context: MenuContext): boolean {
  return (
    !context.possuiTenant &&
    context.isIntegrador &&
    (hasPerm(Permission.CONTA_CREATE, context.authorities) ||
      hasPerm(Permission.INTEGRADOR_RECADO_READ, context.authorities))
  );
}

/** SMS do integrador: depende também da flag de SMS habilitado, além da permissão de integrador. */
function isVisivelSms(context: MenuContext): boolean {
  return (
    hasPerm(Permission.INTEGRADOR_READ, context.authorities) &&
    context.isIntegrador &&
    context.smsHabilitado
  );
}

/** Home some para integrador com licença ACCESS (não superusuário): esse plano não tem home própria. */
function isVisivelHome(context: MenuContext): boolean {
  return !isLicencaAccessOnPro(context.user, context.tenant) || !context.isIntegrador;
}

/**
 * Porta a primeira metade de `MenuRoutes.createCadastros` do Angular: itens
 * empurrados antes de `criarMenusQuePrecisamDoTenant()` no original (Home até
 * Auditoria). A segunda metade (Integradores, Relatórios, LGPD, Shield) é
 * `buildCadastrosTailSection`, que só entra depois da seção de tenant — o
 * Angular intercala as duas chamando `criarMenusQuePrecisamDoTenant()` no meio
 * do método, e reproduzir isso é o que mantém itens como o LGPD no fim do menu.
 */
function buildCadastrosHeadSection(context: MenuContext): MenuItem[] {
  const home = menuItem({
    title: 'Home',
    path: '/home/',
    icon: 'centericon centericon-home',
    show: hasPerm(Permission.PESSOA_READ, context.authorities),
  });

  const inicioIntegrador = menuItem({
    title: 'INICIO',
    path: '/integrador/inicio/',
    icon: 'centericon centericon-recados',
    show: isVisivelInicioIntegrador(context),
  });

  const subIntegrador = menuItem({
    title: 'USUARIOS',
    path: '/sub-integrador/list-sub-integrador',
    icon: 'centericon centericon-pessoas',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const conta = menuItem({
    title: 'Contas',
    path: '/conta/list-conta',
    icon: 'centericon centericon-database',
    show: isVisivelSemTenant(context, Permission.CONTA_READ),
  });

  const contrato = menuItem({
    title: 'Contratos',
    path: '/contrato/list-contrato',
    icon: 'centericon centericon-contratos',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const sms = menuItem({
    title: 'SMS',
    path: '/integrador/sms-view',
    icon: 'centericon centericon-sms',
    show: isVisivelSms(context),
  });

  const gestaoPreCadastros = menuItem({
    title: 'GESTAO_PRE_CADASTROS',
    path: '/gestao-pre-cadastros/list-pre-cadastros',
    icon: 'centericon centericon-pessoas-plus',
    show: isVisivelSemTenant(context, Permission.CONTA_CREATE),
    pointAttention: true,
  });

  const customizacaoIntegrador = menuItem({
    title: 'Customização',
    path: '/integrador/custom-skin-integrador',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const autenticacao2FA = menuItem({
    title: 'Autenticação',
    path: '/integrador/config-autenticacao',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const addWhatsAppComponent = menuItem({
    title: 'WhatsApp',
    path: '/integrador/add-whatsapp',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const addPreCadastroComponent = menuItem({
    title: 'Pré-cadastro',
    path: '/integrador/add-pre-cadastro',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const configuracaoIntegrador = menuItem({
    title: 'Configurações',
    class: 'has-sub',
    icon: 'centericon centericon-configuracoes',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
    submenu: [
      autenticacao2FA,
      customizacaoIntegrador,
      addWhatsAppComponent,
      addPreCadastroComponent,
    ],
  });

  const recadosIntegrador = menuItem({
    title: 'Recados',
    path: '/integrador/recado/list-recado/',
    icon: 'centericon centericon-recados',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_RECADO_READ),
  });

  const logNotificacao = menuItem({
    title: 'Integração PSIM',
    path: '/log-notificacao/list-log-notificacao/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const logDispositivo = menuItem({
    title: 'Dispositivos',
    path: '/log-notificacao/list-log-dispositivos/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const logOperacoes = menuItem({
    title: 'Log de Operações',
    path: '/log-operacoes/list-log-operacoes/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const logWebhook = menuItem({
    title: 'Webhook',
    path: '/log-webhook/list-log-webhook/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const logEventoPsimErro = menuItem({
    title: 'Descartes PSIM',
    path: '/log-evento-psim-erro/list-log-evento-psim-erro/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
  });

  const versoesApps = menuItem({
    title: 'Versões APPs',
    path: '/versao-app-loja/list-versao-app-loja',
    tenant: false,
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelSuperUsuarioSemTenant(context),
  });

  const logsErros = menuItem({
    title: 'Log de erros',
    path: '/log-consulta-erro',
    tenant: false,
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelSuperUsuarioSemTenant(context),
  });

  const auditoria = menuItem({
    title: 'Auditoria',
    icon: 'centericon centericon-auditoria',
    class: 'has-sub',
    show: isVisivelIntegradorSemTenant(context, Permission.INTEGRADOR_READ),
    submenu: [logNotificacao, logDispositivo, logOperacoes, logWebhook, logEventoPsimErro, versoesApps, logsErros],
  });

  const items: MenuItem[] = [];

  if (isVisivelHome(context)) {
    items.push(home);
  }

  items.push(inicioIntegrador);
  items.push(conta);
  items.push(subIntegrador);
  items.push(contrato);
  items.push(sms);
  items.push(gestaoPreCadastros);
  items.push(recadosIntegrador);
  items.push(configuracaoIntegrador);
  items.push(auditoria);

  return items;
}

/**
 * Porta a segunda metade de `MenuRoutes.createCadastros` do Angular: itens
 * empurrados depois de `criarMenusQuePrecisamDoTenant()` no original
 * (Integradores, Relatórios do integrador, LGPD, Relatórios do distribuidor,
 * Shield). Entra depois de `buildTenantSection` em `buildMenuTree`.
 */
function buildCadastrosTailSection(context: MenuContext): MenuItem[] {
  const integrador = menuItem({
    title: 'Integradores',
    path: '/integrador/list-integrador',
    icon: 'centericon centericon-integradores',
    show: hasPerm(Permission.INTEGRADOR_CREATE, context.authorities),
  });

  const relatorioAcesso = menuItem({
    title: 'Primeiro acesso',
    path: '/relatorio/relatorio-primeiro-acesso',
    icon: 'centericon centericon-subitem-mais',
    show:
      !context.possuiTenant &&
      context.isIntegrador &&
      hasPerm(Permission.CONTA_READ, context.authorities),
  });

  const relatorioChamadas = menuItem({
    title: 'Chamadas',
    path: '/relatorio/relatorio-chamadas-integrador',
    icon: 'centericon centericon-subitem-mais',
    show:
      !context.possuiTenant &&
      context.isIntegrador &&
      hasPerm(Permission.CONTA_READ, context.authorities),
  });

  const relatorioLgpdIntegrador = menuItem({
    title: 'LGPD',
    path: '/relatorio/relatorio-lgpd-integrador',
    icon: 'centericon centericon-subitem-mais',
    show:
      (!context.possuiTenant &&
        context.isIntegrador &&
        hasPerm(Permission.INTEGRADOR_READ, context.authorities)) ||
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
  });

  // Menu de relatórios do integrador: exibido sem conta ativa, logo não depende
  // de tenant. Marcá-lo como `tenant` faria o filtro descartá-lo por falta de
  // identificador quando nenhuma conta está selecionada.
  const relatorios = menuItem({
    title: 'Relatórios',
    icon: 'centericon centericon-relatorios',
    class: 'has-sub',
    show:
      !context.possuiTenant &&
      context.isIntegrador &&
      hasPerm(Permission.CONTA_READ, context.authorities),
    submenu: [relatorioChamadas, relatorioAcesso, relatorioLgpdIntegrador],
  });

  const lgpdTermosUso = menuItem({
    title: 'Termos de uso',
    path: '/lgpd/list-lgpd-termos-uso/',
    icon: 'centericon centericon-subitem-mais',
    show:
      (!context.possuiTenant &&
        context.isIntegrador &&
        hasPerm(Permission.INTEGRADOR_READ, context.authorities)) ||
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
  });

  const lgpdPoliticaPrivacidade = menuItem({
    title: 'Política de privacidade',
    path: '/lgpd/list-lgpd-politica-privacidade/',
    icon: 'centericon centericon-subitem-mais',
    show:
      (!context.possuiTenant &&
        context.isIntegrador &&
        hasPerm(Permission.INTEGRADOR_READ, context.authorities)) ||
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
  });

  const lgpd = menuItem({
    title: context.lgpdLabel ?? 'LGPD',
    icon: 'centericon centericon-lgpd',
    class: 'has-sub',
    show:
      (!context.possuiTenant &&
        context.isIntegrador &&
        hasPerm(Permission.INTEGRADOR_READ, context.authorities)) ||
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
    submenu: [lgpdTermosUso, lgpdPoliticaPrivacidade],
  });

  const relatorioLgpdDistribuidor = menuItem({
    title: 'LGPD',
    path: '/relatorio/relatorio-lgpd-distribuidor',
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.INTEGRADOR_CREATE, context.authorities),
  });

  // Menu de distribuidor: exibido sem conta ativa, logo não depende de tenant.
  // Marcá-lo como `tenant` faria o filtro descartá-lo por falta de identificador
  // quando nenhuma conta está selecionada.
  const relatorioLgpd = menuItem({
    title: 'Relatórios',
    icon: 'centericon centericon-relatorios',
    class: 'has-sub',
    show: hasPerm(Permission.INTEGRADOR_CREATE, context.authorities),
    submenu: [relatorioLgpdDistribuidor],
  });

  const redirectShield = menuItem({
    title: 'Segware Shield',
    path: '/integrador/shield',
    icon: 'centericon centericon-segware-shield',
    show: !context.possuiTenant && context.isIntegrador,
  });
  const modoDebug = menuItem({
    title: 'CAPTURA_TELA',
    path: '/modo-debug/list-modo-debug',
    icon: 'centericon centericon-subitem-mais',
    show:
      !context.possuiTenant &&
      context.isIntegrador &&
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
    newFeature: true,
  });
  const suporte = menuItem({
    title: 'Suporte',
    icon: 'icon-menu-suporte',
    class: 'has-sub',
    show:
      !context.possuiTenant &&
      context.isIntegrador &&
      hasPerm(Permission.INTEGRADOR_READ, context.authorities),
    submenu: [modoDebug],
  });

  return [integrador, relatorios, lgpd, relatorioLgpd, redirectShield, suporte];
}

function isDGUARDHabilitado(context: MenuContext): boolean {
  return context.configModulos?.habilitarDGuard === true;
}

function isHabilitarVMS(context: MenuContext): boolean {
  return (
    context.isIntegrador &&
    isDGUARDHabilitado(context) &&
    hasPerm(Permission.CONFIGURAR_CAMERA_CREATE, context.authorities)
  );
}

function isHabilitarCftv(context: MenuContext): boolean {
  return context.isIntegrador && context.configModulos?.habilitarCftv === true;
}

function isHabilitarPSIM(context: MenuContext): boolean {
  return (
    context.isIntegrador &&
    context.configModulos?.habilitarPSIM === true &&
    hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities)
  );
}

function isHabilitarPrevisaoVisita(context: MenuContext): boolean {
  if (context.isIntegrador || !context.possuiUnidadeAssociada) {
    return false;
  }
  return hasPerm(Permission.PREVISAO_VISITA_READ, context.authorities);
}

// Recebem o tenant já garantido: quem chama guarda pelo próprio `tenant`, e não por
// `possuiTenant`, para o TypeScript estreitar o tipo sem um `?.` defensivo cujo ramo
// nulo nunca executaria.
function isVoip(tenant: TenantStorage): boolean {
  return tenant.voipApp === true;
}

function isVoipSip(tenant: TenantStorage): boolean {
  return tenant.tipoVoipApp === 'SIP';
}

// Predicados de visibilidade extraídos de `buildTenantSection`: cada `show`
// combinando `&&`/`||` inline soma na complexidade cognitiva da função que
// monta os itens; nomear as combinações mais comuns (e as específicas de um
// único item) move essa complexidade para funções de uma linha, sem alterar
// o resultado.

/** Item visível apenas com tenant ativo, sujeito à permissão informada. */
function isVisivelComTenant(context: MenuContext, permission: Permission): boolean {
  return context.possuiTenant && hasPerm(permission, context.authorities);
}

/** Item visível apenas para usuário não-integrador, sujeito à permissão informada. */
function isVisivelNaoIntegrador(context: MenuContext, permission: Permission): boolean {
  return !context.isIntegrador && hasPerm(permission, context.authorities);
}

function ehAdministrador(context: MenuContext): boolean {
  return hasPerm(Permission.PESSOA_CREATE, context.authorities);
}

/** Migração: exclusiva de superusuário, e só com tenant ativo. */
function isVisivelMigracao(context: MenuContext): boolean {
  return context.possuiTenant && context.isSuperUsuario;
}

/** Aluguel: com tenant, para integrador ou quem tem permissão de pessoa/inquilino. */
function isVisivelAluguel(context: MenuContext): boolean {
  return (
    context.possuiTenant &&
    (context.isIntegrador ||
      hasPerm(Permission.PESSOA_CREATE, context.authorities) ||
      hasPerm(Permission.INQUILINO_READ, context.authorities))
  );
}

/** Entrega autônoma: com tenant, módulo habilitado na conta e permissão de leitura. */
function isVisivelEntregaAutonoma(context: MenuContext): boolean {
  return (
    context.possuiTenant &&
    context.configModulos?.habilitarEntregaAutonoma === true &&
    hasPerm(Permission.ENTREGA_AUTONOMA_READ, context.authorities)
  );
}

/** Correspondências (visão de unidade): quem só tem leitura, sem o acesso de criação. */
function isVisivelCorrespondenciasUnidade(context: MenuContext): boolean {
  return (
    hasPerm(Permission.CORRESPONDENCIA_READ, context.authorities) &&
    !hasPerm(Permission.CORRESPONDENCIA_CREATE, context.authorities)
  );
}

/** Zelador IA: com tenant, módulo habilitado na conta e permissão de leitura. */
function isVisivelZeladorIA(context: MenuContext): boolean {
  return (
    context.possuiTenant &&
    context.configModulos?.habilitarZeladorIA === true &&
    hasPerm(Permission.ZELADOR_IA_READ, context.authorities)
  );
}

/** Timeline: apenas não-integrador, com o PSIM habilitado na sessão. */
function isVisivelTimeline(context: MenuContext): boolean {
  return !context.isIntegrador && context.psimHabilitado;
}

/** Câmeras (menu do D-Guard): não-integrador, módulo habilitado e permissão de leitura. */
function isVisivelCamerasMenu(context: MenuContext): boolean {
  return (
    !context.isIntegrador &&
    isDGUARDHabilitado(context) &&
    hasPerm(Permission.CAMERA_READ, context.authorities)
  );
}

function isVisivelVMS(context: MenuContext): boolean {
  return context.possuiTenant && isHabilitarVMS(context);
}

function isVisivelCftv(context: MenuContext): boolean {
  return context.possuiTenant && isHabilitarCftv(context);
}

function isVisivelPSIM(context: MenuContext): boolean {
  return context.possuiTenant && isHabilitarPSIM(context) && !isContaPacAutonoma(context.tenant);
}

/** Relatório de agendamentos: com tenant, para quem cria ou atualiza áreas comuns. */
function isVisivelRelatorioAgendamento(context: MenuContext): boolean {
  return (
    context.possuiTenant &&
    (hasPerm(Permission.AREA_COMUM_CREATE, context.authorities) ||
      hasPerm(Permission.AREA_COMUM_UPDATE, context.authorities))
  );
}

/** Relatório de chamadas: com tenant, VoIP habilitado e permissão de leitura de registros. */
function isVisivelRelatorioChamadasVoip(context: MenuContext): boolean {
  return (
    context.tenant !== null &&
    isVoip(context.tenant) &&
    hasPerm(Permission.RELATORIO_REGISTRO_CHAMADA, context.authorities)
  );
}

/** Porta `addSubMenuNotificacaoTipoEvento` do Angular; `null` quando a condição não é satisfeita. */
function buildNotificacaoTipoEventoItem(context: MenuContext): MenuItem | null {
  const isSituatorNovaApi = context.sistemaPSIM === 'SITUATOR_NOVA_API';
  const isSigma = context.sistemaPSIM === 'SIGMA';

  if (!((isSituatorNovaApi && context.webhookHabilitado) || isSigma)) {
    return null;
  }

  return menuItem({
    title: 'Notificações',
    path: '/notificacao-tipo-evento/list-notificacao-tipo-evento/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities),
  });
}

/** Porta `addMenuVoip` do Angular; `[]` quando o guard de VoIP não é satisfeito. */
function buildVoipItems(context: MenuContext): MenuItem[] {
  const { tenant } = context;
  if (tenant === null || !context.isIntegrador || !isVoip(tenant)) {
    return [];
  }

  const dispositivo = menuItem({
    title: 'Dispositivos',
    path: '/equipamento/list-equipamento',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.DISPOSITIVO_READ, context.authorities),
  });

  const configSIP = menuItem({
    title: 'Configurações',
    path: '/voip/config-voip',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const troncos = menuItem({
    title: 'Troncos',
    path: '/voip/list-tronco',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const dispositivoSip = menuItem({
    title: 'Dispositivos',
    path: '/voip/list-dispositivo-sip',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const voip = menuItem({
    title: 'VOIP',
    tenant: true,
    icon: 'centericon centericon-voip',
    class: 'has-sub',
    show: true,
    submenu: isVoipSip(tenant) ? [configSIP, troncos, dispositivoSip] : [dispositivo],
  });

  return [voip];
}

function podeVerAcessoPac(context: MenuContext): boolean {
  return (
    isContaPacAutonoma(context.tenant) &&
    (context.isIntegrador ||
      ehAdministrador(context) ||
      hasPerm(Permission.EQUIPAMENTO_PAC_READ, context.authorities))
  );
}

function podeVerAcessoLegado(context: MenuContext, tenant: TenantStorage): boolean {
  return !isContaPacAutonoma(tenant) && context.isIntegrador && tenant.controleAcesso === true;
}

function buildPacAcessoSubmenu(context: MenuContext): MenuItem[] {
  const gestorDaConta = context.isIntegrador || ehAdministrador(context);
  const items: MenuItem[] = [];

  if (hasPerm(Permission.EQUIPAMENTO_PAC_READ, context.authorities)) {
    items.push(
      menuItem({
        title: 'EQUIPAMENTOS',
        path: '/equipamento-pac/list-equipamento-pac',
        tenant: false,
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
  }

  if (gestorDaConta) {
    items.push(
      menuItem({
        title: 'PERFIS_DE_ACESSO',
        path: '/perfil-acesso/list-perfil-acesso',
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
    items.push(
      menuItem({
        title: 'Credenciais',
        path: '/credencial/list-credenciais',
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
  }

  if (gestorDaConta && hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities)) {
    items.push(
      menuItem({
        title: 'Notificações',
        path: '/notificacao-tipo-evento/list-notificacao-tipo-evento/',
        tenant: true,
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
  }

  if (gestorDaConta && hasPerm(Permission.CONVITE_VIRTUAL_READ, context.authorities)) {
    items.push(
      menuItem({
        title: 'CONVITE_VIRTUAL',
        path: '/convite-virtual',
        tenant: true,
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
  }

  if (gestorDaConta && hasPerm(Permission.CHAVE_VIRTUAL_READ, context.authorities)) {
    items.push(
      menuItem({
        title: 'Chave Virtual',
        path: '/chave-virtual',
        tenant: true,
        icon: 'centericon centericon-subitem-mais',
        show: true,
      }),
    );
  }

  return items;
}

function buildLegadoAcessoSubmenu(context: MenuContext): MenuItem[] {
  const dispositivo = menuItem({
    title: 'Dispositivos',
    path: '/hub/list-equipamento-hub',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.DISPOSITIVO_READ, context.authorities),
  });

  const porta = menuItem({
    title: 'RELES',
    path: '/hub/porta-hub',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const horarios = menuItem({
    title: 'Horários',
    path: '/horarios/list-horarios',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const perfis = menuItem({
    title: 'Perfis',
    path: '/perfil-acesso/list-perfil-acesso',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const credenciais = menuItem({
    title: 'Credenciais',
    path: '/credencial/list-credenciais',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const statusHub = menuItem({
    title: 'STATUS_DISPOSITIVO',
    path: '/hub/status-equipamento-hub',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const logEventosHub = menuItem({
    title: 'Eventos',
    path: '/hub/log-eventos-hub',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const logSincronizacaoHub = menuItem({
    title: 'SINCRONIZACAO',
    path: '/hub/log-sincronizacao-hub',
    icon: 'centericon centericon-subitem-mais',
    show: true,
  });

  const logsHub = menuItem({
    title: "Log's",
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    class: 'has-sub',
    show: true,
    submenu: [logEventosHub, logSincronizacaoHub],
  });

  return [dispositivo, porta, horarios, perfis, credenciais, statusHub, logsHub];
}

function buildAcessosItems(context: MenuContext): MenuItem[] {
  const { tenant } = context;
  if (tenant === null) {
    return [];
  }

  if (podeVerAcessoPac(context)) {
    return [
      menuItem({
        title: 'CONTROLE_DE_ACESSO',
        tenant: true,
        icon: 'centericon centericon-controle-acesso',
        class: 'has-sub',
        show: true,
        submenu: buildPacAcessoSubmenu(context),
      }),
    ];
  }

  if (podeVerAcessoLegado(context, tenant)) {
    return [
      menuItem({
        title: 'AUTONOMO_TITULO',
        tenant: true,
        icon: 'novoLayout centericon-arrows-minimize',
        class: 'has-sub',
        show: true,
        submenu: buildLegadoAcessoSubmenu(context),
      }),
    ];
  }

  return [];
}

/** Porta `MenuRoutes.criarMenusQuePrecisamDoTenant` do Angular; itens na mesma ordem de push do original. */
function buildTenantSection(context: MenuContext): MenuItem[] {
  // Plano ACCESS de integrador não tem os menus dependentes de conta.
  if (isLicencaAccessOnPro(context.user, context.tenant) && context.isIntegrador) {
    return [];
  }

  const migracao = menuItem({
    title: 'Migração',
    path: '/migracao',
    tenant: true,
    icon: 'centericon centericon-migracao',
    show: isVisivelMigracao(context),
  });

  const unidades = menuItem({
    title: 'Unidades',
    path: '/unidade/list-unidade',
    tenant: true,
    icon: 'centericon centericon-unidades',
    show: hasPerm(Permission.UNIDADE_READ, context.authorities),
  });

  const pessoa = menuItem({
    title: 'Pessoas',
    path: '/pessoa/list-pessoa/',
    tenant: true,
    icon: 'centericon centericon-pessoas',
    show: hasPerm(Permission.PESSOA_CREATE, context.authorities),
  });

  const inquilino = menuItem({
    title: 'Aluguel',
    path: '/inquilino/list-inquilino',
    tenant: true,
    icon: 'centericon centericon-home',
    show: isVisivelAluguel(context),
  });

  const previsaoVisita = menuItem({
    title: 'Previsões de visita',
    path: '/portaria/list-portaria',
    icon: 'centericon centericon-previsoes_de_visita',
    show: isHabilitarPrevisaoVisita(context),
  });

  const areaComum = menuItem({
    title: 'Área comum',
    path: '/area-comum/list-area-comum/',
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.AREA_COMUM_READ, context.authorities),
  });

  const grupoPessoa = menuItem({
    title: 'Grupos',
    path: '/grupo-pessoa/list-grupo-pessoa',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.GRUPO_READ),
  });

  const estacionamento = menuItem({
    title: 'Estacionamentos',
    path: '/estacionamento/list-estacionamento/',
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.ESTACIONAMENTO_READ, context.authorities),
  });

  const fornecedor = menuItem({
    title: 'Fornecedor',
    path: '/fornecedor/list-fornecedor/',
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.FORNECEDORES_READ, context.authorities),
  });

  const categoriaChamado = menuItem({
    title: 'Categoria de ocorrência',
    path: '/chamado/list-categoria-chamado/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.CATEGORIA_CHAMADO_READ),
  });

  const entregaAutonoma = menuItem({
    title: 'ENTREGA_AUTONOMA',
    path: '/locker/list-entrega-autonoma',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelEntregaAutonoma(context),
  });

  const cadastros = menuItem({
    title: 'Cadastros',
    tenant: true,
    icon: 'centericon centericon-cadastros',
    class: 'has-sub',
    show: hasPerm(Permission.PESSOA_READ, context.authorities),
    submenu: [
      areaComum,
      grupoPessoa,
      estacionamento,
      fornecedor,
      categoriaChamado,
      entregaAutonoma,
    ],
  });

  const agendamento = menuItem({
    title: 'Agendamentos',
    path: '/agendamento/list-agendamento/',
    icon: 'centericon centericon-input_data',
    show: isVisivelNaoIntegrador(context, Permission.AGENDAMENTO_PROPRIO_READ),
  });

  const recados = menuItem({
    title: 'Recados',
    path: '/recado/list-recado/',
    icon: 'centericon centericon-recados',
    show: isVisivelNaoIntegrador(context, Permission.RECADO_READ),
  });

  const chamado = menuItem({
    title: 'Ocorrências',
    path: '/chamado/list-chamado/',
    tenant: true,
    icon: 'centericon centericon-ocorrencias',
    show: isVisivelNaoIntegrador(context, Permission.CHAMADO_READ),
  });

  const correspondencia = menuItem({
    title: 'Correspondências',
    path: '/correspondencia/list-correspondencias/',
    icon: 'centericon centericon-correspondencias',
    show: isVisivelNaoIntegrador(context, Permission.CORRESPONDENCIA_CREATE),
  });

  const correspondenciasUnidade = menuItem({
    title: 'Correspondências',
    path: '/correspondencia/list-correspondencias-unidade/',
    icon: 'centericon centericon-correspondencias',
    show: isVisivelCorrespondenciasUnidade(context),
  });

  const documentos = menuItem({
    title: 'Documentos',
    path: '/documento/list-documentos/',
    icon: 'centericon centericon-documentos',
    show: hasPerm(Permission.DOCUMENTO_READ, context.authorities),
  });

  const manutencaoPreventiva = menuItem({
    title: 'Manutenção (Beta)',
    path: '/manutencao-preventiva/list-manutencao-preventiva/',
    icon: 'centericon centericon-manutencao_preventiva',
    show: isVisivelComTenant(context, Permission.MANUTENCAO_PREVENTIVA_READ),
  });

  const zeladorIA = menuItem({
    title: 'ZELADOR_IA',
    path: '/zelador-ia/',
    tenant: true,
    icon: 'centericon centericon-zelador-ia',
    show: isVisivelZeladorIA(context),
    newFeature: true,
  });

  const assembleia = menuItem({
    title: 'Assembleia',
    path: '/assembleia/list-assembleia',
    icon: 'centericon centericon-assembleia',
    show: isVisivelNaoIntegrador(context, Permission.ASSEMBLEIA_READ),
  });

  const enquetes = menuItem({
    title: 'ENQUETES',
    path: '/enquete/list-enquetes/',
    icon: 'centericon centericon-enquete',
    show: hasPerm(Permission.ENQUETE_READ, context.authorities),
  });

  const timelineEventos = menuItem({
    title: 'Timeline',
    path: '/timeline-eventos',
    icon: 'centericon centericon-timeline',
    show: isVisivelTimeline(context),
  });

  const portariaFuncionario = menuItem({
    title: 'Portaria',
    path: '/portaria-funcionario/list-portaria-funcionario/',
    tenant: true,
    icon: 'centericon centericon-portaria',
    show: isVisivelNaoIntegrador(context, Permission.PORTARIA_READ),
  });

  const configCameras = menuItem({
    title: 'Conexões',
    path: '/config-cameras/list-configcameras/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.CONFIGURAR_CAMERA_READ),
  });

  const cameras = menuItem({
    title: 'Câmeras',
    path: '/cameras/list-cameras/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CAMERA_READ, context.authorities),
  });

  const lpr = menuItem({
    title: 'LPR',
    path: '/cameras/lpr-cameras/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.CAMERA_READ),
  });

  const vms = menuItem({
    title: 'VMS',
    path: '/submenu-cameras/',
    tenant: true,
    icon: 'centericon centericon-camera_vms',
    class: 'has-sub',
    show: isVisivelVMS(context),
    submenu: [configCameras, cameras, lpr],
  });

  const cftv = menuItem({
    title: 'CFTV',
    path: '/cftv/list-cftv',
    tenant: true,
    icon: 'centericon centericon-camera_cftv',
    show: isVisivelCftv(context),
  });

  const camerasMenu = menuItem({
    title: 'Câmeras',
    path: '/cameras/list-cameras-menu/',
    tenant: true,
    icon: 'centericon centericon-cameras',
    show: isVisivelCamerasMenu(context),
  });

  const psimConfig = menuItem({
    title: 'Conexão',
    path: '/config-psim/step-configpsim/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities),
  });

  const conexaoBiMulticonta = menuItem({
    title: 'Conexão BI',
    path: '/config_bi_multiconta/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: context.ehMulticonta,
  });

  const psimEventos = menuItem({
    title: 'Eventos',
    path: '/evento/list-psim-eventos/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.EVENTO_READ, context.authorities),
  });

  const scondkey = menuItem({
    title: 'Key',
    path: '/config-scond/list-key/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities),
  });

  const chavevirtual = menuItem({
    title: 'Chave virtual',
    path: '/config-scond/list-config-zonas/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURAR_PSIM_READ, context.authorities),
  });

  const psim = menuItem({
    title: 'PSIM',
    path: '/submenu-psim/',
    tenant: true,
    icon: 'centericon centericon-psim',
    class: 'has-sub',
    show: isVisivelPSIM(context),
    submenu: [
      psimConfig,
      conexaoBiMulticonta,
      psimEventos,
      buildNotificacaoTipoEventoItem(context),
      scondkey,
      chavevirtual,
    ].filter((item): item is MenuItem => item !== null),
  });

  const locker = menuItem({
    title: 'Locker',
    path: '/locker/app-locker/',
    tenant: true,
    icon: 'centericon centericon-locker',
    show: isVisivelComTenant(context, Permission.DISPOSITIVO_READ),
  });

  const configuracoes = menuItem({
    title: 'Configurações',
    path: '/configuracoes/list-configuracoes/',
    tenant: true,
    icon: 'centericon centericon-configuracoes',
    show: isVisivelNaoIntegrador(context, Permission.CONFIGURACOES_CREATE),
  });

  const configuracoesConta = menuItem({
    title: 'Conta',
    path: '/configuracoes/list-configuracoes-conta/',
    tenant: true,
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURACOES_CREATE, context.authorities),
  });

  const modulos = menuItem({
    title: 'Integrações',
    path: '/configmodulos/',
    icon: 'centericon centericon-subitem-mais',
    show: hasPerm(Permission.CONFIGURACOES_READ, context.authorities),
  });

  const configsituatorMenu = menuItem({
    title: 'Configurações',
    icon: 'centericon centericon-configuracoes',
    class: 'has-sub',
    show: hasPerm(Permission.CONFIGURAR_PSIM_CREATE, context.authorities),
    submenu: [configuracoesConta, modulos],
  });

  const relatorioAcionamentos = menuItem({
    title: 'ACIONAMENTOS',
    path: '/relatorio/relatorio-acionamentos',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.RELATORIO_ABERTURA_PORTA),
  });

  const relatorioAgendamento = menuItem({
    title: 'Agendamentos',
    path: '/relatorio/relatorio-agendamentos/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelRelatorioAgendamento(context),
  });

  const relatorioChamadas = menuItem({
    title: 'Chamadas',
    path: '/relatorio/relatorio-chamadas',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelRelatorioChamadasVoip(context),
  });

  const relatorioChamado = menuItem({
    title: 'Ocorrência',
    path: '/relatorio/relatorio-chamado/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.CHAMADO_UPDATE),
  });

  const relatorioPortaria = menuItem({
    title: 'Portaria',
    path: '/relatorio/relatorio-portaria/',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.PORTARIA_READ),
  });

  const relatorioPrevisaoVisita = menuItem({
    title: 'Previsão de visita',
    path: '/relatorio/relatorio-previsao-visita',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.PREVISAO_VISITA_CREATE),
  });

  const relatorioPrimeiroAcessoConta = menuItem({
    title: 'Primeiro acesso',
    path: '/relatorio/relatorio-primeiro-acesso-conta',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.RELATORIO_PRIMEIRO_ACESSO_CREATE),
  });

  const relatorioUnidades = menuItem({
    title: 'Unidades',
    path: '/relatorio/relatorio-unidades',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.RELATORIO_UNIDADE),
  });

  const relatorioVeiculos = menuItem({
    title: 'Veiculos',
    path: '/relatorio/relatorio-veiculos',
    icon: 'centericon centericon-subitem-mais',
    show: isVisivelComTenant(context, Permission.RELATORIO_VEICULO_READ),
  });

  const relatorios = menuItem({
    title: 'Relatórios',
    tenant: true,
    icon: 'centericon centericon-relatorios',
    class: 'has-sub',
    show: context.possuiTenant,
    submenu: [
      relatorioAcionamentos,
      relatorioAgendamento,
      relatorioChamadas,
      relatorioChamado,
      relatorioPortaria,
      relatorioPrevisaoVisita,
      relatorioPrimeiroAcessoConta,
      relatorioUnidades,
      relatorioVeiculos,
    ],
  });

  return [
    migracao,
    unidades,
    pessoa,
    inquilino,
    cadastros,
    agendamento,
    recados,
    chamado,
    correspondencia,
    correspondenciasUnidade,
    documentos,
    manutencaoPreventiva,
    zeladorIA,
    assembleia,
    enquetes,
    timelineEventos,
    portariaFuncionario,
    vms,
    cftv,
    camerasMenu,
    psim,
    locker,
    configuracoes,
    configsituatorMenu,
    previsaoVisita,
    ...buildVoipItems(context),
    ...buildAcessosItems(context),
    relatorios,
  ];
}

/**
 * Constrói a árvore completa do menu a partir do contexto hidratado. Porta
 * `MenuRoutes.createRoutes`: a metade inicial de Cadastros, a seção dependente
 * de tenant e a metade final de Cadastros, na mesma ordem do Angular (que
 * chama `criarMenusQuePrecisamDoTenant()` no meio de `createCadastros()`).
 */
export function buildMenuTree(context: MenuContext): MenuItem[] {
  return [
    ...buildCadastrosHeadSection(context),
    ...buildTenantSection(context),
    ...buildCadastrosTailSection(context),
  ];
}
